// RequestRechargeForm — Phase 10 (2026-05-12) full rewrite.
//
// Inline collapsible card embedded in MyWalletPanel AND MyDepositPanel.
// The user picks WHICH wallet they're topping up via the `target`
// prop:
//   target='wallet'  → credits agent_wallet  (operating pool)
//   target='deposit' → credits agent_deposit_wallet (collateral)
//
// What the agent now sees BEFORE the form fields (the gap the user
// called out — bare form with no parent QR):
//   1. Their UPLINE's sub-agent QR + UPI + WhatsApp, fetched via
//      /admin/me-agent/emergency-recharge. The endpoint returns the
//      parent's `subagent_*` QR (Phase 9 dual-QR system), with a
//      per-field fallback to the parent's player-facing QR if they
//      haven't uploaded a distinct sub-agent QR yet.
//   2. A UPI / Cash payment-method toggle. UPI keeps the existing
//      txn-id flow; Cash relaxes the minimum length of the
//      transaction_id field (it's a cash-receipt marker rather
//      than a UPI reference).
//
// On submit: POST /admin/me-agent/recharge with
// { amount, transaction_id, note, target, payment_method }. The
// backend routes to the right wallet on parent/admin approval.

import { FormEvent, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import WhatsAppIcon from '@baaligames/shared-ui/components/WhatsAppIcon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { useT } from '@baaligames/shared-ui/lib/i18n';
// Cascade Recharge Lock (Phase R7 follow-up, 2026-05-14). Calm panel
// shown in place of ParentPaymentCard + form body when the server
// reports parent_locked=true on /admin/me-agent/emergency-recharge.
import RechargeLockedPanel from '@baaligames/shared-ui/components/RechargeLockedPanel';
import ProofImagePicker from '@baaligames/shared-ui/components/ProofImagePicker';

// 2026-05-18 — Identity masking. The backend's
// /admin/me-agent/emergency-recharge response now ships a structured
// `display_name` field on the parent block (composed server-side
// from first_name + last_name). The `username` field is gone.
type ParentInfo = {
  role: 'admin' | 'agent';
  id: number;
  display_name: string;
  uaid: string | null;
  whatsappNumber: string | null;
  supportPhone: string | null;
  upiId: string | null;
  qrImageUrl: string | null;
  displayNote: string | null;
};

type Props = {
  /** Called after a successful submit so the parent panel can refresh. */
  onSubmitted: () => void;
  onCancel: () => void;
  /** Which wallet this recharge feeds. Defaults to 'wallet'. */
  target?: 'wallet' | 'deposit';
};

export default function RequestRechargeForm({
  onSubmitted, onCancel, target = 'wallet',
}: Props) {
  const t = useT();
  const [parent, setParent] = useState<ParentInfo | null>(null);
  const [parentErr, setParentErr] = useState<string | null>(null);
  // Cascade Recharge Lock (Phase R7 follow-up, 2026-05-14). When the
  // server reports the immediate parent agent as auto-suspended, the
  // emergency-recharge response carries parent_locked=true with every
  // payment field redacted. The form replaces ParentPaymentCard +
  // the amount/txn/note inputs + the submit button with a calm
  // RechargeLockedPanel. The Cancel button in the banner is left in
  // place so the user can dismiss the form. The submit endpoint
  // stays open server-side (defender's compromise — protects users
  // who paid before the suspension landed; see depositGuards header)
  // but the UI does not surface a submit affordance.
  const [parentLocked, setParentLocked] = useState(false);
  // Phase R7 follow-up (2026-05-14): the same endpoint surfaces a
  // distinct lock_reason so we can swap the calm-panel variant
  // between "cascade lock" (parent over capacity) and
  // "not_configured" (parent hasn't activated their sub-agent QR).
  // Default to cascade_lock if the flag is set but reason is
  // missing — that's the legacy behavior before this feature.
  const [lockReason, setLockReason] = useState<'temporary_recharge_outage' | 'not_configured' | null>(null);
  // Cascade Recharge Lock — Dynamic Capacity. The same endpoint that
  // tells us whether the parent is locked also returns a positive
  // capacity number when the parent has a NEGATIVE wallet but enough
  // cushion left for a recharge above MIN. null = no cap (happy path
  // or fully locked — distinguished by parentLocked).
  const [remainingCapacity, setRemainingCapacity] = useState<number | null>(null);

  const [paymentMethod, setPaymentMethod] = useState<'upi' | 'cash'>('upi');
  const [amount, setAmount] = useState('');
  const [txn, setTxn] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Phase 18 — optional payment screenshot.
  const [proofBlob, setProofBlob] = useState<Blob | null>(null);

  // Fetch the parent QR / UPI on mount. Same endpoint the lockout
  // portal uses — returns the parent's sub-agent QR (Phase 9).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await adminApi.get<{
          parent: ParentInfo | null;
          parent_locked?: boolean;
          lock_reason?: 'temporary_recharge_outage' | 'not_configured' | null;
          remaining_capacity?: number | null;
        }>('/admin/me-agent/emergency-recharge');
        if (cancelled) return;
        setParent(r.parent);
        setParentLocked(!!r.parent_locked);
        setLockReason(r.lock_reason ?? null);
        setRemainingCapacity(r.remaining_capacity ?? null);
      } catch (e) {
        if (!cancelled) setParentErr((e as ApiError).message || t('agent.recharge.parent.loadError'));
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    const amt = Number(amount);
    if (!Number.isInteger(amt) || amt < 10) {
      setErr(t('agent.recharge.error.amountMin')); setBusy(false); return;
    }
    // Cascade Recharge Lock — Dynamic Capacity client-side guard.
    // Server will reject with EXCEEDS_CAPACITY too (source of truth),
    // but failing fast here saves a round-trip and gives a more
    // immediate UX response.
    if (remainingCapacity != null && amt > remainingCapacity) {
      setErr(t('recharge.cap.tooHigh', {
        max: '₹' + remainingCapacity.toLocaleString('en-IN'),
      }));
      setBusy(false); return;
    }
    const minTxn = paymentMethod === 'cash' ? 2 : 6;
    if (txn.trim().length < minTxn) {
      setErr(paymentMethod === 'cash'
        ? t('agent.recharge.error.txnCash')
        : t('agent.recharge.error.txnUpi'));
      setBusy(false); return;
    }
    try {
      const r = await adminApi.post<{ request: { id: number } }>(
        '/admin/me-agent/recharge',
        {
          amount: amt,
          transaction_id: txn.trim(),
          note: note.trim() || undefined,
          target,
          payment_method: paymentMethod,
        }
      );
      // Phase 18 — if a screenshot was selected, upload it now.
      // Non-fatal: the request is already saved; agent can retry
      // the upload from history within 24h.
      if (proofBlob && r?.request?.id) {
        try {
          const fd = new FormData();
          fd.append('proof', proofBlob, 'screenshot.jpg');
          await adminApi.postForm(`/admin/me-agent/recharge/${r.request.id}/proof`, fd);
        } catch (_uploadErr) {
          // Surface a soft warning but DON'T block the success path.
          setErr('Request submitted, but screenshot upload failed. Open History within 24 h to retry.');
        }
      }
      setAmount(''); setTxn(''); setNote(''); setProofBlob(null);
      onSubmitted();
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }

  const isDeposit = target === 'deposit';
  const accentClasses = isDeposit
    ? {
        banner:   'bg-amber-50/70 dark:bg-amber-400/10',
        bannerIcon: 'text-amber-700 dark:text-amber-300',
        eyebrow:  'text-amber-700 dark:text-amber-300',
        focus:    'focus:border-amber-400 focus:ring-amber-400/30',
        submit:   'bg-amber-500 hover:bg-amber-400 active:bg-amber-600 shadow-[0_2px_10px_rgba(245,158,11,0.4)]',
      }
    : {
        banner:   'bg-brand-50/70 dark:bg-brand-400/10',
        bannerIcon: 'text-brand-700 dark:text-brand-300',
        eyebrow:  'text-brand-700 dark:text-brand-300',
        focus:    'focus:border-brand-400 focus:ring-brand-400/30',
        submit:   'bg-brand-500 hover:bg-brand-400 active:bg-brand-600 shadow-[0_2px_10px_rgba(var(--brand-rgb),0.4)]',
      };

  return (
    <motion.form
      onSubmit={submit}
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.22 }}
      className="overflow-hidden rounded-2xl
                 bg-white dark:bg-arena-700/40
                 border border-arena-200/60 dark:border-arena-600/40
                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none"
    >
      {/* Banner */}
      <div className={`flex items-start gap-2.5 px-4 py-3 border-b border-arena-200/40 dark:border-arena-600/40 ${accentClasses.banner}`}>
        <Icon name="arrow_upward" size={16} className={`${accentClasses.bannerIcon} mt-0.5 shrink-0`} filled />
        <div className="min-w-0 flex-1">
          <div className={`text-[11px] uppercase tracking-wider font-bold ${accentClasses.eyebrow}`}>
            {t(isDeposit ? 'agent.recharge.banner.eyebrow.deposit' : 'agent.recharge.banner.eyebrow.wallet')}
          </div>
          <div className="text-[12.5px] text-arena-700 dark:text-neon-100/90 leading-snug">
            {t(isDeposit ? 'agent.recharge.banner.body.deposit' : 'agent.recharge.banner.body.wallet')}
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="grid place-items-center w-7 h-7 rounded-full
                     hover:bg-arena-100/60 dark:hover:bg-arena-600/55 transition shrink-0"
          aria-label={t('agent.recharge.banner.closeAria')}
        >
          <Icon name="close" size={15} className="text-arena-600 dark:text-neon-200/70" />
        </button>
      </div>

      {/* Parent payment instructions card */}
      <div className="px-4 pt-4">
        {/* Cascade Recharge Lock — parent_locked TRUE swap. We check
            this BEFORE the parent-truthy branch because the server
            still returns a parent identity object for friendly
            display purposes; it just zeroes every payment field. */}
        {parentLocked ? (
          <RechargeLockedPanel
            variant={lockReason === 'not_configured' ? 'not_configured' : 'cascade_lock'}
          />
        ) : parent ? (
          <ParentPaymentCard parent={parent} />
        ) : parentErr ? (
          <div className="rounded-xl bg-rose-50 dark:bg-rose-500/15 border border-rose-200 dark:border-rose-400/30 text-rose-700 dark:text-rose-200 px-3 py-2 text-[12px]">
            {parentErr}
          </div>
        ) : (
          <div className="rounded-xl bg-arena-50/40 dark:bg-arena-700/30 border border-arena-200/60 dark:border-arena-600/40 px-3 py-3 text-[12px] text-arena-600 dark:text-neon-200/70 text-center">
            <Icon name="progress_activity" size={14} className="animate-spin inline-block mr-1.5" />
            {t('agent.recharge.parent.loading')}
          </div>
        )}
      </div>

      {/* Form body is suppressed while parent_locked — no amount,
          no txn id input, no submit button. The Cancel button in
          the banner above remains the only action the user can
          take. The Cancel here in the form body is also redundant
          when the form body is hidden, so we collapse both. */}
      {!parentLocked && (
      <div className="p-4 space-y-3">
        {/* Cascade Recharge Lock — Dynamic Capacity banner. Shown
            only when the parent has a positive cap (i.e., wallet<0
            but cushion left). The banner reads as informational
            rather than warning — the user isn't IN trouble, they
            just have a temporary upper bound. */}
        {remainingCapacity != null && (
          <div className="rounded-xl bg-amber-50 dark:bg-amber-500/10
                          border border-amber-300 dark:border-amber-400/30
                          px-3 py-2 flex items-start gap-2">
            <Icon name="info" size={16} className="text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" filled />
            <div className="text-[12.5px] leading-snug text-amber-900 dark:text-amber-100">
              {t('recharge.cap.banner', {
                max: '₹' + remainingCapacity.toLocaleString('en-IN'),
              })}
            </div>
          </div>
        )}

        {/* Payment-method toggle */}
        <PaymentMethodToggle value={paymentMethod} onChange={setPaymentMethod} />

        <Field label={t('agent.recharge.field.amount.label')}>
          <input
            type="number" inputMode="numeric"
            className={`${inputCls(accentClasses.focus)} tabular-nums`}
            value={amount}
            onChange={(e) => {
              // Clamp on every keystroke when a cap is in force, so
              // the field can never hold a value above the parent's
              // current capacity. The submit handler ALSO checks
              // (defense in depth), but clamping here makes the cap
              // self-evident to the user.
              const raw = e.target.value;
              if (remainingCapacity != null && raw !== '') {
                const n = Number(raw);
                if (Number.isFinite(n) && n > remainingCapacity) {
                  setAmount(String(remainingCapacity));
                  return;
                }
              }
              setAmount(raw);
            }}
            placeholder={t('agent.recharge.field.amount.placeholder')}
            min={10} step={1}
            max={remainingCapacity != null ? remainingCapacity : undefined}
          />
        </Field>

        <Field label={paymentMethod === 'cash'
          ? t('agent.recharge.field.txn.label.cash')
          : t('agent.recharge.field.txn.label.upi')}>
          <input
            className={`${inputCls(accentClasses.focus)} font-mono`}
            value={txn}
            onChange={(e) => setTxn(e.target.value)}
            placeholder={paymentMethod === 'cash'
              ? t('agent.recharge.field.txn.placeholder.cash')
              : t('agent.recharge.field.txn.placeholder.upi')}
            maxLength={80}
          />
        </Field>

        <Field label={t('agent.recharge.field.note.label')}>
          <input
            className={inputCls(accentClasses.focus)}
            value={note} maxLength={190}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('agent.recharge.field.note.placeholder')}
          />
        </Field>

        {/* Phase 18 — optional payment screenshot. Same widget the
            player flow uses. Canvas re-encode strips polyglots
            client-side; server sharp re-encodes again. Skipping is
            fine — agent has 24h to attach from history if they do. */}
        <ProofImagePicker
          onFile={(blob) => setProofBlob(blob)}
          onClear={() => setProofBlob(null)}
          onError={(m) => setErr(m)}
          label="Upload screenshot of the payment for fast action."
          hint="Optional — you can also attach it from History within 24 hours of submitting."
        />

        {err && (
          <div className="rounded-xl bg-rose-50 dark:bg-rose-500/15 border border-rose-200 dark:border-rose-400/30 text-rose-700 dark:text-rose-200 px-3 py-2 text-[12.5px]">
            {err}
          </div>
        )}

        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-3 py-2 rounded-full text-[12px] font-semibold
                       bg-white/60 dark:bg-arena-700/40
                       text-arena-700 dark:text-neon-200/80
                       border border-arena-200/60 dark:border-arena-600/50
                       hover:bg-white/90 dark:hover:bg-arena-600/55 transition"
          >
            {t('agent.recharge.cta.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !amount || !txn}
            className={`ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                       text-white text-[13px] font-bold
                       transition active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed
                       ${accentClasses.submit}`}
          >
            {busy
              ? <Icon name="progress_activity" size={14} className="animate-spin" />
              : <Icon name="send" size={14} />}
            {t('agent.recharge.cta.submit')}
          </button>
        </div>
      </div>
      )}
    </motion.form>
  );
}

// ─── Sub-components ─────────────────────────────────────────────

function ParentPaymentCard({ parent }: { parent: ParentInfo }) {
  const t = useT();
  return (
    <div className="rounded-xl bg-arena-50/60 dark:bg-arena-700/30
                    border border-arena-200/60 dark:border-arena-600/50 p-3">
      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-arena-600 dark:text-neon-200/80 mb-2">
        {t(parent.role === 'admin' ? 'agent.recharge.parent.label.admin' : 'agent.recharge.parent.label.parentAgent')}
      </div>
      <div className="flex items-start gap-3">
        {parent.qrImageUrl ? (
          <a href={parent.qrImageUrl} target="_blank" rel="noreferrer"
             className="block shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-lg overflow-hidden
                        border border-arena-200/60 dark:border-arena-600/50 bg-white">
            <img src={parent.qrImageUrl} alt="Payment QR" className="w-full h-full object-contain" />
          </a>
        ) : (
          <div className="shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-lg
                          bg-arena-100/60 dark:bg-arena-700/40
                          grid place-items-center text-[10.5px] text-arena-600 dark:text-neon-200/70 text-center px-1">
            {t('agent.recharge.parent.noQr')}
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-1.5">
          <div>
            <div className="text-[14px] font-bold text-arena-900 dark:text-white truncate">
              {/* 2026-05-18 — Display name, never username. */}
              <span className={(parent.display_name || '').trim() ? '' : 'opacity-60'}>
                {(parent.display_name || '').trim() || '—'}
              </span>
              {parent.uaid ? ` · ${parent.uaid}` : ''}
            </div>
            <div className="text-[10.5px] text-arena-600 dark:text-neon-200/75">
              {t(parent.role === 'admin' ? 'agent.recharge.parent.role.admin' : 'agent.recharge.parent.role.parentAgent')}
            </div>
          </div>
          {parent.upiId && (
            <div className="text-[12px] break-all">
              <span className="text-arena-600 dark:text-neon-200/75 mr-1">{t('agent.recharge.parent.upi')}</span>
              <span className="font-mono text-arena-900 dark:text-white">{parent.upiId}</span>
            </div>
          )}
          {parent.whatsappNumber && (
            <a href={`https://wa.me/${parent.whatsappNumber.replace(/\D/g, '')}`}
               target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700 dark:text-emerald-300 hover:underline">
              <WhatsAppIcon size={12} />
              {parent.whatsappNumber}
            </a>
          )}
          {parent.displayNote && (
            <p className="text-[11px] text-arena-700 dark:text-neon-200/70 italic leading-snug pt-0.5">
              "{parent.displayNote}"
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function PaymentMethodToggle({
  value, onChange,
}: { value: 'upi' | 'cash'; onChange: (v: 'upi' | 'cash') => void }) {
  const t = useT();
  return (
    <div>
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">
        {t('agent.recharge.method.eyebrow')}
      </span>
      <div className="grid grid-cols-2 gap-2">
        <PaymentMethodPill
          active={value === 'upi'}
          onClick={() => onChange('upi')}
          icon="qr_code_2"
          label={t('agent.recharge.method.upi.label')}
          note={t('agent.recharge.method.upi.note')}
        />
        <PaymentMethodPill
          active={value === 'cash'}
          onClick={() => onChange('cash')}
          icon="payments"
          label={t('agent.recharge.method.cash.label')}
          note={t('agent.recharge.method.cash.note')}
        />
      </div>
    </div>
  );
}

function PaymentMethodPill({
  active, onClick, icon, label, note,
}: {
  active: boolean;
  onClick: () => void;
  icon: string;
  label: string;
  note: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-xl px-3 py-2 text-left border-2 transition
        ${active
          ? 'border-brand-400 bg-brand-50/60 dark:bg-brand-400/10 dark:border-brand-400/60 shadow-[0_2px_10px_rgba(var(--brand-rgb),0.2)]'
          : 'border-arena-200/60 dark:border-arena-600/50 bg-white dark:bg-arena-700/30 hover:border-arena-300 dark:hover:border-arena-500/60'}`}
    >
      <div className="flex items-center gap-1.5">
        <span className={`grid place-items-center w-7 h-7 rounded-lg
                          ${active
                            ? 'bg-brand-500 text-white'
                            : 'bg-arena-100 dark:bg-arena-700/60 text-arena-700 dark:text-neon-200/85'}`}>
          <Icon name={icon} size={15} filled />
        </span>
        <span className={`text-[12.5px] font-bold ${active ? 'text-arena-900 dark:text-white' : 'text-arena-700 dark:text-neon-200/80'}`}>
          {label}
        </span>
      </div>
      <div className="text-[10.5px] text-arena-600 dark:text-neon-200/70 mt-1">{note}</div>
    </button>
  );
}

const inputCls = (focus: string) =>
  `w-full rounded-xl px-3 py-2 text-[14px]
   bg-arena-50/40 dark:bg-arena-950/40
   border border-arena-200/60 dark:border-arena-600/50
   text-arena-900 dark:text-white
   placeholder-arena-400 dark:placeholder-neon-200/35
   outline-none focus:ring-2 ${focus}`;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">
        {label}
      </span>
      {children}
    </label>
  );
}
